import { $framework } from "@opendash/core";
import { RouteContext } from "@opendash/router";
import { Upload, UploadProps } from "antd";
import { makeAutoObservable, runInAction } from "mobx";
import {
  JSONData,
  KommuterConverter,
  MessagePush,
} from "../js/kommuter/KommuterConverter";

type Step =
  | "upload"
  | "convert_start"
  | "convert_agency"
  | "convert_routes_form"
  | "convert_routes"
  | "convert_stops"
  | "convert_trips_form"
  | "convert_trips"
  | "convert_stop_times"
  | "convert_complete";

export class KommuterImportPageState {
  ctx: RouteContext;
  kommuterConverter?: KommuterConverter;
  messages: MessagePush[] = [];

  step: Step = "upload";

  rawData: JSONData = {
    agency: {},
    routes: [],
    stops: [],
    stopTimes: [],
    trips: [],
  };

  // form fields
  routeType?: number = -1;
  startDate?: Date = undefined;
  endDate?: Date = undefined;

  constructor(ctx: RouteContext) {
    makeAutoObservable(this);
  }

  public getUploadProps(): UploadProps {
    return {
      name: "file",
      accept: "application/json",
      multiple: false,
      maxCount: 1,
      beforeUpload: (file) => {
        const isAllowed = file.type === "application/json";

        if (!isAllowed) {
          return void $framework.antd_msg.error(`${file.name} is not allowed.`);
        }

        const reader = new FileReader();
        reader.readAsDataURL(file as Blob);

        return isAllowed || Upload.LIST_IGNORE;
      },
      customRequest: ({ filename, file, onSuccess, onError }) => {
        this.handleJSONData(
          new File([file as Blob], filename, {
            type: "application/json",
          })
        );
      },
    };
  }

  public async useKommuterData(data: JSONData) {
    const valid = this.checkJSONData(data);
    if (valid) {
      this.set("rawData", data);
      this.addMessage({
        text: "gtfs:import.kommuter.info.data_accepted",
        status: "success",
      });
      await this.convertAgency();
    }
  }

  private async handleJSONData(file: File) {
    const data = await file.text();
    const parsedData = (await JSON.parse(data)) as typeof this.rawData;
    const valid = this.checkJSONData(parsedData);
    if (valid) {
      this.set("rawData", parsedData);
      this.addMessage({
        text: "gtfs:import.kommuter.info.data_accepted",
        status: "success",
      });
      this.convertAgency();
    }
  }

  private async convertAgency() {
    this.set("step", "convert_start");
    this.set(
      "kommuterConverter",
      new KommuterConverter(
        this.rawData,
        this.addMessage.bind(this),
        this.addMessage.bind(this),
        this.addMessage.bind(this),
        this.addMessage.bind(this)
      )
    );
    this.set("step", "convert_agency");
    await this.kommuterConverter.convertAgency();
    this.initRouteConversion();
  }

  public async initRouteConversion() {
    this.set("step", "convert_routes_form"); // opens a form in frontend for missing data
    this.addMessage({
      text: "gtfs:import.kommuter.info.routes.type.select",
      status: "secondary",
    });
  }

  public async convertRoutes() {
    this.set("step", "convert_routes");
    await this.kommuterConverter.convertRoutes(this.routeType);

    this.convertStops();
  }

  public async convertStops() {
    this.set("step", "convert_stops");
    await this.kommuterConverter.convertStops();

    this.initTripsConversion();
  }

  private async initTripsConversion() {
    this.addMessage({
      text: "Bitte gebe an, zu welchem Datum die Fahrten beginnen und enden sollen.",
      status: "secondary",
    });
    this.set("step", "convert_trips_form"); // opens a form in frontend for missing data
  }

  public async convertTrips() {
    this.set("step", "convert_trips");
    await this.kommuterConverter.convertTrips(this.startDate, this.endDate);
    this.set("step", "convert_stop_times");

    this.convertStopTimes();
  }

  public async convertStopTimes() {
    this.set("step", "convert_stop_times");
    await this.kommuterConverter.convertStopTimes();
    this.set("step", "convert_complete");
    this.addMessage({
      status: "success",
      text: "Import erfolgreich abgeschlossen.",
    });
  }

  private checkJSONData(data: typeof this.rawData) {
    let isValid = true;
    for (const key in this.rawData) {
      if (!data[key]) {
        this.set("messages", [
          ...this.messages,
          {
            text: `gtfs:import.kommuter.error.missing.${key}`,
            status: "danger",
          },
        ]);
        isValid = false;
      }
    }
    return isValid;
  }

  private addMessage(message: MessagePush) {
    this.set("messages", [...this.messages, message]);
  }

  public set<K extends keyof this>(key: K, value: this[K]): void {
    runInAction(() => {
      this[key] = value;
    });
  }
}
